import React, { useState, useEffect } from 'react'
import { arrayOf, bool, func, number, shape, string } from 'prop-types'
import { i18n } from '@fs/zion-locale'
import { css } from '@emotion/core'
import { Separator, useContainerWidth, Paragraph, BillboardText, colors, H3, H5, H6 } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import DOMPurify from 'dompurify'
import NameSelector from '../NameSelector'
import populateChoices from '../name_selector_helper'
import { getTileContentData, getVariantData } from '../../../services/services'

const cssAttribution = css`
  color: #bbb;
  font-size: 11px;
  padding-top: 10px;
  padding-inline-end: 10px;
  padding-bottom: 10px;
  padding-inline-start: 0;
`

const ATTRIBUTED_LANGUAGES = ['en', 'fr']
export default function NameMeaning({ region, id, parsedNames, tileContentData, nameArgs, tileContentDispatch }) {
  const [t] = useTranslation()
  const atWidth = useContainerWidth()
  const [nameArg, setNameArg] = useState(nameArgs ?? { name: parsedNames?.firstName?.name, isFirst: true })
  const [variantArray, setVariantArray] = useState([])
  const [variantDefinition, setVariantDefinition] = useState('')
  const [variantChips, setVariantChips] = useState([])
  const [variantName, setVariantName] = useState('')
  const [variantAttribution, setVariantAttribution] = useState('')
  const tileData = tileContentData
  const [hasMeaning] = [tileData] ?? [{}]
  const { htmlDefinition, originLanguage, name, variants } = hasMeaning ?? { htmlDefinition: null }
  const { COGNATE } = variants ?? [{}]
  const { PET } = variants ?? [{}]
  const { SHORT } = variants ?? [{}]
  const { VARIANT } = variants ?? [{}]
  const { REFERENCE } = variants ?? [{}]

  const nameCustomsData = {
    pt_: {
      heading: 'Sobrenomes na língua portuguesa',
      body: 'Quatro influências desempenharam um papel no desenvolvimento de sobrenomes na língua portuguesa; sobrenomes baseados nos nomes dos pais, na ocupação da pessoa, sobrenomes descrevendo alguma qualidade única da pessoa ou sobrenomes com base em onde a pessoa residia. No Brasil, os sobrenomes portugueses eram frequentemente dados a pessoas nativas e escravizadas quando eram batizadas. Muitas vezes o povo brasileiro terá sobrenomes duplos. Exemplos são: Maria Ferreira de Castilhos, José João Costa Silva e Francisco Rosa e Silva. Geralmente, o primeiro sobrenome veio da mãe e o último sobrenome veio do pai. Em muitos casos, um sobrenome foi arbitrariamente adotado. Rancores familiares, sobrenomes populares, nomes relacionados a um local, o desejo de evitar conexões familiares indesejáveis, ou o desejo de expressar apreciação ou simpatia a alguém resultou em mudanças de sobrenome.',
    },
    ws_: {
      heading: "O ma aga iloga o le fa'aigoaina",
      body: "O tu ma aga iloga o le fa'aigoaina o i Samoa matuai e 'ese mai ia i latou o aganu'u Anglo/Europa e fa'aigoaina. O le igoa Samoa e mafai ona aofia ai ni vaega ese'ese, o le matia po o fa'alagiga na tu'uina mai e le āiga (aiga/clan), o se igoa na tu'uina mai e mafai ona aofia ai igoa e uiga i uiga, uiga mana'omia, ma isi mea fa'apena. Sa tu'uina atu ia Matai i ali'i ma tamaitai uma o le togatuga, ae o le tele lava o taimi e tele lava ina tu'uina atu i ali'i. Sa masani ona i ai se igoa na tu'uina atu i tagata talavou ma sa fa'ae'e atu le matai i se taimi mulimuli ane a o fa'asolo ina matutua. O le igoa muamua lava o se isi tagata o le aiga e pe'i o se tama po o se tuagane o le aiga(tāma)po o se tina po o se tuafafine o le aiga(tinā). E muamua lava ona tu'uina atu le matai, a o le'i tu'uina atu le igoa muamua. Afai e tele ni matai na tu'uina atu, atonu e le tutusa i taimi uma le fa'atulagaga a le matai ma le poloa'iga ua tu'uina atu. O mea taua e tutupu i le olaga e masani ona tu'uina mai ai se mafua'aga e su'ia ai le igoa ua tu'uina mai. Ina ua filifilia se igoa fou na tu'uina atu i ai, sa masani lava ona lafoai le feagaiga tuai. Ina ua maea le nofoiaina o Europa, sa vaetamaina e nisi o samoa ia fa'aiu o styled i Europa.",
    },
    no_: {
      heading: 'Norske etternavn',
      body: 'Norske etternavn var bredt basert på tre metoder; patronymisk - basert på farens navn, som Jensen (sønn av Jens); geografisk - basert på en persons gårdnavn, for eksempel Mundal; eller yrke - basert på personens yrke, for eksempel Smed (Smith). Den dominerende typen etternavn i Norge er patronymisk. Etternavnet endret seg med hver generasjon. Jon Arnesen var for eksempel sønn av en mann ved navn Arne. Jons søstre ville fått Jonsdatter (datter av Jon) som etternavn. Ofte identifiseres personer i opptegnelsene ved navn og bosted; ved navn og patronymisk etternavn; eller ved navn, patronymisk etternavn og bosted. Når gårdsnavn er oppgitt i en opptegnelse, gir de informasjon om oppholdssted, og er ikke en del av personens etternavn. Kulturelt har en person bare ett navn (eller fornavn), men det kan bestå av flere navn, for eksempel Kathinka Ovidia Isabella. I Norge ville det bli sett på som personens hele fornavn. Noen ganger fikk to eller flere barn i en familie samme navn. I noen tilfeller ble det gjort fordi et eldre barn døde og det neste barnet av samme kjønn fikk navnet. Imidlertid kunne to eller flere barn med samme navn også ha levd til voksen alder.',
    },
    pr: {
      heading: 'Las costumbres de los nombres',
      body: "Las costumbres de los nombres de Puerto Rico siguen el mismo modelo utilizado por la mayor parte de Hispanoamérica y España. Puerto Rico practicaba la convención de poner nombres empleando dos apellidos. El primer apellido es el primer apellido del padre y el segundo apellido es el primer apellido de la madre. La palabra 'y', que significa 'además', se puede encontrar entre los apellidos. Una mujer casada podía agregar el apellido de su marido a su nombre completo añadiendo 'de', que significa 'esposa de', al final de su nombre completo y luego el primer apellido de su marido. Muchos pueblos indígenas recibieron nombres y apellidos hispanos tradicionales por sacerdotes católicos cuando se convirtieron. Otros a veces retenían nombres que tenían anteriormente. Históricamente, los nombres de pila tendían a ser nombres cristianos y un niño podría haber sido dado de uno a tres nombres de pila.",
    },
  }

  const selectedName = nameArg?.name ?? ''
  const selectedIsFirst = nameArg?.isFirst
  const choices = populateChoices({ selected: selectedName, names: parsedNames, isFirst: selectedIsFirst })
  const language = i18n.language

  const arrayCheck = (arr) => {
    if (arr && arr.length > 0) {
      return true
    }
    return false
  }

  // eslint-disable-next-line react/no-unstable-nested-components
  const PetNames = () => (
    <div style={{ display: 'flex', flexWrap: 'wrap', margin: '5px 0' }}>
      {t('aam.name-meaning.pet', 'Pet')} :
      {PET.map((petName, i, arr) => (
        <strong style={{ marginInlineStart: '7px' }} key={petName.name}>
          {arr.length === i + 1 ? `${petName.name}.` : `${petName.name},`}
        </strong>
      ))}
    </div>
  )

  // eslint-disable-next-line react/no-unstable-nested-components
  const ShortNames = () => (
    <div style={{ display: 'flex', flexWrap: 'wrap', margin: '5px 0' }}>
      {t('aam.name-meaning.short-form', 'Short form')} :
      {SHORT.map((shortName, i, arr) => (
        <strong style={{ marginInlineStart: '7px' }} key={shortName.name}>
          {arr.length === i + 1 ? `${shortName.name}.` : `${shortName.name},`}
        </strong>
      ))}
    </div>
  )

  // eslint-disable-next-line react/no-unstable-nested-components
  const CognateNames = () => (
    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
      <p>
        {t('aam.name-meaning.cognates', 'Cognates')} :{' '}
        {COGNATE.map((cognate) => (
          <React.Fragment key={cognate.preHtmlName + cognate.name}>
            {cognate.preHtmlName || ''}
            <strong style={{ margin: '0 7px' }}>{`${cognate.name}.  ` || ''}</strong>
          </React.Fragment>
        ))}
      </p>
    </div>
  )

  // eslint-disable-next-line react/no-unstable-nested-components
  const VariantNames = () => (
    <div style={{ marginTop: '12px' }}>
      <strong>{t('aam.name-meaning.possible-related', 'Possibly related names')}:</strong>
      <NameSelector
        justify="left"
        choices={variantChips}
        selectionChange={(selectedChip) => {
          const newVariantChips = variantChips
          newVariantChips.forEach((chip) => {
            if (selectedChip.value === chip.value) {
              chip.selected = true
            } else {
              chip.selected = false
            }
          })
          setVariantChips(newVariantChips)
          const definition = variantArray.find((variation) => variation.data.name === selectedChip.value)
          setVariantDefinition(definition.data.htmlDefinition)
          setVariantName(definition.data.name)
          setVariantAttribution(definition.data.attribution)
        }}
      />
    </div>
  )

  // eslint-disable-next-line react/no-unstable-nested-components
  const VariantDefinition = () => (
    <>
      <BillboardText primaryColor={colors.white}>
        <H5>{variantName}</H5>
      </BillboardText>
      <div
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: variantDefinition
            ? DOMPurify.sanitize(variantDefinition)
            : t('aam.name-meaning.name-not-found', "You have a unique name.  We couldn't find any matches!"),
        }}
      />
    </>
  )

  useEffect(() => {
    const chippyChips = []
    if (arrayCheck(variantArray)) {
      variantArray.forEach((data) => {
        if (data.headers['x-match-type'] === 'exact' && data.data.name !== name) {
          chippyChips.push({
            label: data.data.name,
            value: data.data.name,
            name: data.data.name,
            isFirst: data.data.type === 'FIRST',
            selected: false,
          })
        }
      })
      setVariantChips(chippyChips)
    }
  }, [variantArray, name])

  let attribution
  let origin

  if (originLanguage === 'en') {
    origin = 'English'
  }

  if (language === 'en') {
    attribution =
      selectedName === parsedNames?.lastName?.name
        ? t('aam.name-meaning.surname-attribution')
        : t('aam.name-meaning.name-attribution')
  } else if (language === 'fr') {
    attribution = t('aam.name-meaning.geneanet-attribution')
  }

  useEffect(() => {
    if (arrayCheck(VARIANT) || arrayCheck(REFERENCE)) {
      let variantRefArray
      if (arrayCheck(REFERENCE) && arrayCheck(VARIANT)) {
        variantRefArray = [...VARIANT, ...REFERENCE]
      } else if (arrayCheck(REFERENCE)) {
        variantRefArray = REFERENCE
      } else if (arrayCheck(VARIANT)) {
        variantRefArray = VARIANT
      }
      getVariantData({
        nameArray: variantRefArray,
        firstName: nameArg.isFirst,
      }).then((res) => {
        setVariantArray(res)
      })
    }
  }, [VARIANT, REFERENCE, nameArg])

  useEffect(() => {
    if (nameArgs) {
      setVariantArray([])
      setVariantDefinition('')
      setVariantChips([])
      setVariantName('')
      setVariantAttribution('')
      setNameArg(nameArgs)
    }
  }, [nameArgs])

  const mainStyle = css`
    padding-top: 0;
    padding-inline-end: 3vw;
    padding-bottom: 3vw;
    padding-inline-start: 3vw;
    flex-grow: 1;
    display: flex;
    align-items: top;
    flex-direction: column;
  `
  const nameCustomsCss = css`
    margin-top: 15px;
  `

  return (
    <div css={mainStyle}>
      <Separator size="md" />
      <NameSelector
        justify="center"
        choices={choices}
        selectionChange={(selectedChip) => {
          setVariantArray([])
          setVariantDefinition('')
          setVariantChips([])
          setVariantName('')
          setVariantAttribution('')
          setNameArg({ name: selectedChip.value, isFirst: selectedChip.isFirst })

          getTileContentData({
            supportedTileId: 'namemeaning',
            countryCode: region,
            tileId: id,
            nameArgs: { name: selectedChip.value, isFirst: selectedChip.isFirst },
          }).then((res) => {
            tileContentDispatch({ type: 'complete', res })
          })
        }}
      />
      <div style={{ display: 'flex' }}>
        <H3>{name ?? selectedName}</H3>
        {name && selectedName !== name && (
          <div style={{ position: 'relative' }}>
            <p css={{ color: 'grey', margin: atWidth({ default: '15px 0 0 10px', xl: '23px 0 0 10px' }) }}>
              {t('aam.name-meaning.similar', `Similar to ${selectedName}`, { name: selectedName })}
            </p>
          </div>
        )}
      </div>
      {origin && <Paragraph>{origin}</Paragraph>}
      <div
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: htmlDefinition
            ? DOMPurify.sanitize(htmlDefinition)
            : t('aam.name-meaning.name-not-found', "You have a unique name.  We couldn't find any matches!"),
        }}
      />
      {PET && <PetNames />}
      {SHORT && <ShortNames />}
      {COGNATE && <CognateNames />}
      {(VARIANT || REFERENCE) && variantChips.length > 0 && <VariantNames />}
      {(VARIANT || REFERENCE) && variantDefinition && <VariantDefinition />}
      {ATTRIBUTED_LANGUAGES.includes(language) &&
        (VARIANT || REFERENCE) &&
        variantAttribution &&
        variantAttribution.replace('.', '') !== attribution && <div css={cssAttribution}>{variantAttribution}</div>}
      {ATTRIBUTED_LANGUAGES.includes(language) && <div css={cssAttribution}>{attribution}</div>}
      {nameCustomsData[region] && (
        <div css={nameCustomsCss}>
          <H6>{nameCustomsData[region].heading}</H6>
          <Paragraph size="sm">{nameCustomsData[region].body}</Paragraph>
        </div>
      )}
    </div>
  )
}

NameMeaning.propTypes = {
  region: string,
  id: number,
  parsedNames: shape({
    firstName: shape({}),
    lastName: shape({}),
    middleNames: arrayOf(shape({ name: string })),
  }),
  tileContentData: shape({
    superType: string,
    meaning: string,
    name: string,
    head: string,
    add: string,
    typ: string,
    yr: number,
    reg: string,
    lang: string,
    cat: string,
    catid: number,
    scat: string,
  }),
  nameArgs: shape({
    name: string,
    isFirst: bool,
  }),
  tileContentDispatch: func,
}
